import type { MockMessage } from "@/dummy";

/**
 * Builds a plain-text `.txt` transcript of a conversation from the widget's
 * mapped message list — the same `MockMessage[]` the thread renders. The BE
 * exposes the FULL conversation (unpaginated) via `visitorChatTranscript`; the
 * widget OWNS the text format so wording, the visitor's locale/timezone, and
 * the media placeholders stay a front-end concern (no BE i18n).
 *
 * Shape (a standard transcript export):
 *
 *   {title}
 *   {startedLine}
 *
 *   ---
 *
 *   {HH:MM AM} | {sender}: {content}
 *   {HH:MM AM} | {sender}: {content}
 *
 *   ---
 *   {exportedLine}
 */

/** Scoped translator (e.g. `useTranslations("transcript")`). */
type Translate = (key: string, values?: Record<string, string | number>) => string;

export interface BuildTranscriptOptions {
  /** Operator's organisation / app name for the header + footer lines. */
  orgName: string;
  /** How the visitor is labelled in their own lines — email, then name, then a
   *  generic fallback (already resolved by the caller). */
  visitorLabel: string;
  /** Resolved AI agent name for ai/bot lines (`resolveAiName(...) ?? default`). */
  aiName: string;
  /** BCP-47 locale for date/time formatting (the visitor's UI locale). */
  locale: string;
  /** IANA timezone; defaults to the runtime's resolved zone. */
  timeZone?: string;
  /** Export timestamp — defaults to now. */
  now?: Date;
  /** True when the BE capped the conversation (adds a truncation note). */
  truncated?: boolean;
  /** Scoped i18n translator for the wrapper copy. */
  t: Translate;
}

/** Placeholder text for a non-text attachment, keyed off its mimetype. */
export function mediaPlaceholder(mimetype: string): string {
  if (mimetype === "image/gif") return "[GIF]";
  if (mimetype.startsWith("image/")) return "[Image]";
  if (mimetype.startsWith("video/")) return "[Video]";
  if (mimetype.startsWith("audio/")) return "[Audio]";
  if (mimetype === "application/pdf") return "[PDF]";
  return "[File]";
}

/** HTML → plain text, entity-aware via DOMParser with a regex fallback for
 *  sandboxed contexts where DOMParser is unavailable. */
function toPlainText(html: string): string {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body.textContent ?? "").replace(/\s+/g, " ").trim();
  } catch {
    return html
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
}

/** The label shown before a message: visitor → their email/name, ai/bot → the
 *  configured agent name, operator → their own name (or the localized role). */
function senderLabel(message: MockMessage, opts: BuildTranscriptOptions): string {
  const kind = message.sender.kind;
  if (kind === "visitor") return opts.visitorLabel;
  if (kind === "ai" || kind === "bot") return opts.aiName;
  const name = message.sender.name?.trim();
  if (name) return name;
  return opts.t("operator");
}

/** The line body: media placeholders first, then the text (caption), joined by
 *  a space. Empty for a content-less lifecycle row (which the builder skips). */
function messageContent(message: MockMessage): string {
  const media = (message.media ?? []).map((m) => mediaPlaceholder(m.mimetype));
  const text = toPlainText(message.text ?? "");
  return [...media, text].filter(Boolean).join(" ");
}

/** Per-message time — locale-driven 12h/24h (no forced am/pm), so uk reads
 *  "23:54" and en-US "04:37 AM" rather than a hardcoded 12-hour clock. */
function formatTime(date: Date, locale: string, timeZone?: string): string {
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  }).format(date);
}

/**
 * Full localized date-time stamp for the header/footer, e.g.
 *   en-US: "September 17, 2026 at 4:37 AM Eastern European Summer Time (GMT+03:00)"
 *   uk:    "17 вересня 2026 р. о 04:37 за східноєвропейським літнім часом (GMT+03:00)"
 *
 * `dateStyle` + `timeStyle` gives the locale's OWN date↔time connector ("at" /
 * "о") and clock convention — so nothing is hardcoded in English. The timezone
 * name + offset are appended separately (they can't be combined with the
 * style presets).
 */
function formatStamp(date: Date, locale: string, timeZone?: string): string {
  const main = new Intl.DateTimeFormat(locale, {
    dateStyle: "long",
    timeStyle: "short",
    timeZone,
  }).format(date);
  const tz = timeZoneLabel(date, locale, timeZone);
  return tz ? `${main} ${tz}` : main;
}

/** "Pacific Daylight Time (GMT-07:00)" — long zone name + offset, best-effort. */
function timeZoneLabel(date: Date, locale: string, timeZone?: string): string {
  const namePart = (name: "long" | "shortOffset" | "longOffset") => {
    try {
      const parts = new Intl.DateTimeFormat(locale, {
        timeZone,
        timeZoneName: name,
      }).formatToParts(date);
      return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
    } catch {
      return "";
    }
  };
  const long = namePart("long");
  const offset = namePart("longOffset") || namePart("shortOffset");
  if (long && offset) return `${long} (${offset})`;
  return long || offset;
}

export function buildTranscript(messages: MockMessage[], opts: BuildTranscriptOptions): string {
  const { orgName, locale, timeZone, t } = opts;
  const now = opts.now ?? new Date();

  const rows = messages
    .map((m) => {
      const content = messageContent(m);
      if (!content) return null; // lifecycle / empty rows carry nothing to export
      const when = m.createdAt ? new Date(m.createdAt) : null;
      const time = when && !Number.isNaN(when.getTime()) ? formatTime(when, locale, timeZone) : "";
      const who = senderLabel(m, opts);
      return `${time} | ${who}: ${content}`.replace(/^ \| /, ""); // drop empty time prefix
    })
    .filter((line): line is string => line !== null);

  const first = messages.find((m) => m.createdAt)?.createdAt;
  const startedAt = first ? new Date(first) : now;

  const header = [
    t("title", { org: orgName }),
    t("started", { when: formatStamp(startedAt, locale, timeZone) }),
  ].join("\n");

  const footer = [
    ...(opts.truncated ? [t("truncated")] : []),
    t("exported", { org: orgName, when: formatStamp(now, locale, timeZone) }),
  ].join("\n");

  return `${header}\n\n---\n\n${rows.join("\n\n")}\n\n---\n${footer}\n`;
}
